import { memo } from "react";
import { useTranslation } from "react-i18next";
import { Box, Stack } from "@mui/material";
import { Button, Typography } from "@foodpilot/foods";
import type { Permission } from "src/api/client-api/permissions/useGetAllPermissions";
import { ActionToggle, ActionTogglePlaceholder } from "./ActionToggle";

export type PermissionResourceRowProps = {
    object: string;
    coreColumns: string[];
    corePermissions: Record<string, Permission | undefined>;
    specificPermissions: Permission[];
    selectedIds: Set<string>;
    onToggle: (permissionId: string) => void;
    onSelectAll: (object: string) => void;
    onSelectNone: (object: string) => void;
    columnTemplate: string;
    stacked: boolean;
};

const buildAriaLabel = (permission: Permission) => `${permission.slug} ${permission.object}`;

const PermissionResourceRowComponent = (props: PermissionResourceRowProps) => {
    const {
        object,
        coreColumns,
        corePermissions,
        specificPermissions,
        selectedIds,
        onToggle,
        onSelectAll,
        onSelectNone,
        columnTemplate,
        stacked,
    } = props;
    const { t } = useTranslation();

    const existingCorePermissions = coreColumns
        .map((slug) => corePermissions[slug])
        .filter((permission): permission is Permission => permission !== undefined);
    const rowPermissions = [...existingCorePermissions, ...specificPermissions];
    const total = rowPermissions.length;
    const activeCount = rowPermissions.filter((permission) => selectedIds.has(permission["@id"])).length;
    const hasActive = activeCount > 0;

    const header = (
        <Stack role="rowheader" spacing={0.75} sx={(theme) => ({ minWidth: 0, paddingRight: theme.spacing(1) })}>
            <Typography
                variant="body-semibold"
                sx={(theme) => ({ color: theme.custom.grey[2800], overflowWrap: "anywhere" })}
            >
                {object}
            </Typography>
            <Stack direction="row" alignItems="center" spacing={1}>
                <Typography
                    variant="small-bold"
                    sx={(theme) => ({ color: hasActive ? theme.palette.primary[700] : theme.custom.grey[2500] })}
                >
                    {activeCount}/{total}
                </Typography>
                <Button variant="text" size="small" onClick={() => onSelectAll(object)}>
                    {t("settings.roles.bulk.all", "all")}
                </Button>
                <Button variant="text" size="small" onClick={() => onSelectNone(object)}>
                    {t("settings.roles.bulk.none", "none")}
                </Button>
            </Stack>
        </Stack>
    );

    if (stacked) {
        return (
            <Stack
                role="group"
                aria-label={object}
                spacing={1}
                sx={(theme) => ({
                    padding: theme.spacing(1.5, 2),
                    borderTop: `1px solid ${theme.custom.grey[400]}`,
                })}
            >
                {header}
                <Box sx={(theme) => ({ display: "flex", flexWrap: "wrap", gap: theme.spacing(1), minWidth: 0 })}>
                    {rowPermissions.map((permission) => (
                        <ActionToggle
                            key={permission["@id"]}
                            label={permission.slug}
                            ariaLabel={buildAriaLabel(permission)}
                            active={selectedIds.has(permission["@id"])}
                            onToggle={() => onToggle(permission["@id"])}
                        />
                    ))}
                </Box>
            </Stack>
        );
    }

    return (
        <Box
            role="row"
            sx={(theme) => ({
                display: "grid",
                gridTemplateColumns: columnTemplate,
                alignItems: "center",
                columnGap: theme.spacing(1.5),
                padding: theme.spacing(1.5, 2),
                borderTop: `1px solid ${theme.custom.grey[400]}`,
            })}
        >
            {header}

            {coreColumns.map((slug) => {
                const permission = corePermissions[slug];

                if (permission === undefined) {
                    return (
                        <Box role="cell" key={slug} sx={{ display: "flex", justifyContent: "center" }}>
                            <ActionTogglePlaceholder />
                        </Box>
                    );
                }

                const permissionId = permission["@id"];

                return (
                    <Box role="cell" key={slug} sx={{ display: "flex", justifyContent: "center" }}>
                        <ActionToggle
                            label={slug}
                            ariaLabel={buildAriaLabel(permission)}
                            active={selectedIds.has(permissionId)}
                            onToggle={() => onToggle(permissionId)}
                            fullWidth
                        />
                    </Box>
                );
            })}

            <Box
                role="cell"
                sx={(theme) => ({ display: "flex", flexWrap: "wrap", gap: theme.spacing(1), minWidth: 0 })}
            >
                {specificPermissions.length === 0 ?
                    <Box aria-hidden sx={(theme) => ({ height: theme.spacing(4) })} />
                :   specificPermissions.map((permission) => (
                        <ActionToggle
                            key={permission["@id"]}
                            label={permission.slug}
                            ariaLabel={buildAriaLabel(permission)}
                            active={selectedIds.has(permission["@id"])}
                            onToggle={() => onToggle(permission["@id"])}
                        />
                    ))
                }
            </Box>
        </Box>
    );
};

export const PermissionResourceRow = memo(PermissionResourceRowComponent);
